import axios from "axios";
import { useCallback, useState } from "react";
import { getAuthToken } from "../context/AuthContext";

const baseUrl = process.env.REACT_APP_BACKEND_URL + "api/reservations";

const api = axios.create({
  baseURL: baseUrl,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log("=== RESERVATION API RESPONSE SUCCESS ===");
    console.log("Status:", response.status);
    console.log("Data:", response.data);
    console.log("=== END RESERVATION API RESPONSE SUCCESS ===");
    return response;
  },
  (error) => {
    console.log("=== RESERVATION API RESPONSE ERROR ===");
    console.log("Error:", error);
    console.log("Status:", error.response?.status);
    console.log("Response data:", error.response?.data);
    console.log("Request URL:", error.config?.url);
    console.log("Request headers:", error.config?.headers);
    console.log("=== END RESERVATION API RESPONSE ERROR ===");
    return Promise.reject(error);
  },
);

const useReservations = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Get all reservations for current company
   * @param {string} startDate - Optional start date (YYYY-MM-DD)
   * @param {string} endDate - Optional end date (YYYY-MM-DD)
   * @returns {Promise<Array>} Array of reservations
   */
  const getAllReservations = useCallback(
    async (startDate = null, endDate = null) => {
      try {
        setIsLoading(true);
        setError(null);
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;

        const response = await api.get("/", { params });
        return response.data;
      } catch (err) {
        console.error("Error fetching reservations:", err);
        setError(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  /**
   * Get reservations for a specific date range
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<Array>} Array of reservations
   */
  const getReservationsByDateRange = useCallback(
    async (startDate, endDate) => {
      return getAllReservations(startDate, endDate);
    },
    [getAllReservations],
  );

  /**
   * Get a single reservation by ID
   * @param {string} id - Reservation ID
   * @returns {Promise<Object>} Reservation object
   */
  const getReservationById = useCallback(async (id) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(`/${id}`);
      return response.data;
    } catch (err) {
      console.error("Error fetching reservation:", err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Create a new reservation
   * @param {Object} reservationData - Reservation data
   * @returns {Promise<Object>} Created reservation
   */
  const createReservation = useCallback(async (reservationData) => {
    try {
      setIsLoading(true);
      setError(null);
      console.log("Creating reservation with data:", reservationData);
      const response = await api.post("/", reservationData, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (err) {
      console.error("Error creating reservation:", err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Update an existing reservation
   * @param {string} id - Reservation ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated reservation
   */
  const updateReservation = useCallback(async (id, updateData) => {
    try {
      setIsLoading(true);
      setError(null);
      console.log("Updating reservation", id, "with data:", updateData);
      const response = await api.put(`/${id}`, updateData, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (err) {
      console.error("Error updating reservation:", err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Delete a reservation
   * @param {string} id - Reservation ID
   * @returns {Promise<void>}
   */
  const deleteReservation = useCallback(async (id) => {
    try {
      setIsLoading(true);
      setError(null);
      console.log("Deleting reservation:", id);
      await api.delete(`/${id}`);
    } catch (err) {
      console.error("Error deleting reservation:", err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Check if a time slot is available
   * @param {string} date - Date (YYYY-MM-DD)
   * @param {string} startTime - Start time (HH:MM:SS)
   * @param {string} endTime - End time (HH:MM:SS)
   * @returns {Promise<boolean>} True if available
   */
  const checkAvailability = useCallback(async (date, startTime, endTime) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get("/check-availability", {
        params: { date, start_time: startTime, end_time: endTime },
      });
      return response.data.available;
    } catch (err) {
      console.error("Error checking availability:", err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Persoonlijke agendalink (met geheime token) voor het ingelogde bedrijf
  const getICSLink = useCallback(async () => {
    const response = await api.get("/ics-link");
    return response.data.url;
  }, []);

  return {
    getICSLink,
    getAllReservations,
    getReservationsByDateRange,
    getReservationById,
    createReservation,
    updateReservation,
    deleteReservation,
    checkAvailability,
    isLoading,
    error,
  };
};

/**
 * Public API functions (no auth required) for external websites
 */

/**
 * Create a public reservation from external website
 * @param {string} companyId - Company ID
 * @param {Object} reservationData - Reservation data
 * @returns {Promise<Object>} Created reservation response
 */
export const createPublicReservation = async (companyId, reservationData) => {
  try {
    const response = await axios.post(`${baseUrl}/public/book`, {
      company_id: companyId,
      ...reservationData,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating public reservation:", error);
    throw error;
  }
};

/**
 * Get available time slots for a specific date (public endpoint)
 * @param {string} companyId - Company ID
 * @param {string} date - Date (YYYY-MM-DD)
 * @returns {Promise<Object>} Object with date and booked_slots array
 */
export const getPublicAvailability = async (companyId, date) => {
  try {
    const response = await axios.get(`${baseUrl}/public/availability`, {
      params: { company_id: companyId, date },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching public availability:", error);
    throw error;
  }
};

export default useReservations;
